import React, { useState, useEffect } from 'react'

import { withRouter, RouteComponentProps } from 'react-router-dom'

import { Header, Segment } from 'semantic-ui-react'
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Switch,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Link,
  Slider,
  CardActionArea,
  Alert,
} from '@mui/material'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import VideoLibraryOutlinedIcon from '@mui/icons-material/VideoLibraryOutlined'
import DynamicFeedOutlinedIcon from '@mui/icons-material/DynamicFeedOutlined'
import MovieCreationOutlinedIcon from '@mui/icons-material/MovieCreationOutlined'
import CloudDownloadOutlinedIcon from '@mui/icons-material/CloudDownloadOutlined'

import ReactMapboxGl, { ZoomControl, Layer, Feature } from 'react-mapbox-gl'
import ReactDatePicker from 'react-datepicker'

import { MAPBOX_API_KEY } from '../../../config/environments'
import { locales } from '../../../config/constants'
import { DEFAULT_PROJECT_TYPE } from '../../../config/projectTypes'
import { getBbox } from '../../../utils/sharedFunctions'
import { formatNumber } from '../../../utils/sharedFunctions'
import {
  findDuplicateVideoPromptFields,
  isVideoPromptRequired,
  setVideoPromptRequired,
  trimOptionalVideoPromptIndexes,
} from '../../../utils/videoPrompts'
import InviteCard from './InviteCard'
import BrandingCard from './BrandingCard'
import FormatsCard from './FormatsCard'
import SettingsCard from './SettingsCard'
import Data, { IClip, IProject, ProjectQuestionResponse } from '../../../api/Data'
import LegalComplianceCard from './LegalComplianceCard'
import ProjectWorkflowCard from './ProjectWorkflowCard'
import ContestAddonCard from './ContestAddonCard'
import { getOverviewClipImage, replaceBrokenOverviewClipImage } from './overviewClipImage'

import ExternalLink from '../../../components/common/ExternalLink'

import EmptyShelves from '../../../_images/empty-shelves.png'

import moment from 'moment'
import 'react-datepicker/dist/react-datepicker.css'

const MAX_NUMBER_OF_QUESTIONS = 8

const MAX_QUESTIONS_CHARACTER_LENGTH = 260

const styles = {
  paper: {
    padding: '16px',
  },
  statBox: {
    textAlign: 'center',
  },
}

const ExpiryButton = React.forwardRef<
  HTMLButtonElement,
  { value?: string; onClick?: React.MouseEventHandler<HTMLButtonElement> }
>(({ value, onClick }, ref) => (
  <Button
    ref={ref}
    variant="text"
    size="small"
    onClick={onClick}
    sx={{ justifyContent: 'flex-end', textTransform: 'none' }}
  >
    {value || 'Set expiry date'}
  </Button>
))

ExpiryButton.displayName = 'ExpiryButton'

const Map = MAPBOX_API_KEY
  ? ReactMapboxGl({
      accessToken: MAPBOX_API_KEY,
      touchZoomRotate: true,
    })
  : null

const ConfigureTabOverviewPage = (props: IProps) => {
  const {
    history,
    project,
    clips,
    projectQuestions,
    handleChange,
    handleChangePrimaryFont,
    handleChangePrimaryColor,
    validationError,
  } = props

  const [projectTitle, setProjectTitle] = useState(project.title)
  const [projectName, setProjectName] = useState(project.name)
  const [projectReward, setProjectReward] = useState(project.incentive)
  const [projectLocale, setProjectLocale] = useState(project.locale)
  const [numberOfQuestions, setNumberOfQuestions] = useState(project.numberOfQuestions || 1)
  const [questions, setQuestions] = useState<string[]>(project.questions || [])
  const [isShared, setIsShared] = useState(project.shared)
  const [exportCount, setExportCount] = useState(0)

  const projectLinkForQRCode = `${project.projectUrl}?utm_source=qrcode` // utm is used to differentiate direct form qrcode scan

  useEffect(() => {
    setProjectTitle(project.title)
    setProjectName(project.name)
    setProjectReward(project.incentive)
    setProjectLocale(project.locale)
    setNumberOfQuestions(project.numberOfQuestions || 1)
    setQuestions(project.questions || [])
  }, [project])

  useEffect(() => {
    let mounted = true

    Data.getExports(project.slug).then(({ data, error }) => {
      if (!mounted || error) return
      const exports = Array.isArray(data) ? data : data?.rows || []
      setExportCount(exports.length)
    })

    return () => {
      mounted = false
    }
  }, [project.slug])

  const handleProjectTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.title = e.target.value

    setProjectTitle(e.target.value)

    handleChange(e, { name: 'title', value: e.target.value })
  }

  const handleProjectNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.name = e.target.value

    setProjectName(e.target.value)

    handleChange(e, { name: 'name', value: e.target.value })
  }

  const handleProjectRewardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.incentive = e.target.value

    setProjectReward(e.target.value)

    handleChange(e, { name: 'reward', value: e.target.value })
  }

  const handleProjectLocaleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.locale = e.target.value

    setProjectLocale(e.target.value)

    handleChange(e, { name: 'locale', value: e.target.value })
  }

  const handleProjectLiveChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    project.shared = e.target.checked

    setIsShared(e.target.checked)

    handleChange(e, { name: 'shared', value: e.target.checked })
  }

  const handleDeadlineChange = (date: Date | null) => {
    const deadline = date ? moment(date).set('hour', 23).set('minute', 59).set('second', 59).format() : null
    handleChange(date, { name: 'deadline', value: deadline })
  }

  const handlePromptCountChange = (e: Event, value: number | number[]) => {
    const promptCount = Number(value)

    setNumberOfQuestions(promptCount)

    handleChange(e, {
      updates: {
        numberOfQuestions: promptCount,
        displaySetting: trimOptionalVideoPromptIndexes(project.displaySetting, promptCount),
      },
    })
  }

  const handlePromptRequiredChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    promptIndex: number,
    required: boolean
  ) => {
    handleChange(e, {
      name: 'displaySetting',
      value: setVideoPromptRequired(project.displaySetting, promptIndex, required),
    })
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    const questionNumber = parseInt(name.split('-')[1], 10) - 1
    const updatedQuestions = [...questions]

    updatedQuestions[questionNumber] = value
    setQuestions(updatedQuestions)

    handleChange(e, { name: 'questions', values: updatedQuestions })
  }

  const getLatestEntries = () => {
    return clips.slice(0, 6)
  }

  const goToClipGalleryPage = () => {
    history.push(`/projects/${project.slug}/curate`)
  }

  const goToClipPage = (clip: IClip) => {
    history.push(`/projects/${project.slug}/curate?clipId=${clip.id}`)
  }

  const getClipsWithLocation = () => {
    return clips.filter((clip) => clip.lat1 && clip.lon1)
  }

  const bbox: any = getBbox(clips)

  const latestEntries: IClip[] = getLatestEntries()
  const submissionCount = new Set(clips.map((clip) => clip.submissionId || `clip-${clip.id}`)).size

  const clipsWithLocation: IClip[] = getClipsWithLocation()
  const pageViewCount = project.pageViews || 0
  const clipCount = project.clipCount || clips.length || 0
  const compilationCount = project.episodeCount || 0
  const pluralLabel = (count: number, singular: string, plural = `${singular}s`) => (count === 1 ? singular : plural)
  const projectType = project.projectType || DEFAULT_PROJECT_TYPE
  const isCaptureWorkflow = projectType === 'video_capture'
  const isConversationWorkflow = projectType === 'video_conversation'
  const contributorQuestions = projectQuestions.filter((question) => !question.isAskedPerClip)
  const fileQuestions = projectQuestions.filter((question) => !!question.isAskedPerClip)
  const duplicatePromptFields = findDuplicateVideoPromptFields(
    questions.slice(0, isConversationWorkflow ? numberOfQuestions : 1),
    contributorQuestions
  )
  const insightCards = [
    {
      label: pluralLabel(pageViewCount, 'Page View'),
      value: formatNumber(pageViewCount),
      action: 'Open upload page',
      icon: <VisibilityOutlinedIcon />,
      onClick: () => window.open(project.projectUrl, '_blank', 'noopener,noreferrer'),
    },
    {
      label: pluralLabel(submissionCount, 'Submission'),
      value: formatNumber(submissionCount),
      action: submissionCount === 1 ? 'View submission' : 'View submissions',
      icon: <DynamicFeedOutlinedIcon />,
      onClick: goToClipGalleryPage,
    },
    {
      label: pluralLabel(clipCount, 'Clip'),
      value: formatNumber(clipCount),
      action: 'Open video library',
      icon: <VideoLibraryOutlinedIcon />,
      onClick: goToClipGalleryPage,
    },
    {
      label: pluralLabel(compilationCount, 'Compilation'),
      value: formatNumber(compilationCount),
      action: 'Create a story',
      icon: <MovieCreationOutlinedIcon />,
      onClick: () => history.push(`/projects/${project.slug}/combine`),
    },
    {
      label: pluralLabel(exportCount, 'Export'),
      value: formatNumber(exportCount),
      action: 'Open project downloads',
      icon: <CloudDownloadOutlinedIcon />,
      onClick: () => history.push(`/projects/${project.slug}/compile`),
    },
  ]

  const renderQuestionSummary = (question: ProjectQuestionResponse) => {
    const typeLabel =
      question.datasetId && question.datasetTitle
        ? `Lookup · ${question.datasetTitle}`
        : question.type === 'FreeText'
        ? 'Free text'
        : question.type === 'MultiSelect'
        ? 'Multiple select'
        : 'Single select'

    return (
      <Box
        key={question.id}
        sx={{
          alignItems: { xs: 'flex-start', sm: 'center' },
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 1,
          justifyContent: 'space-between',
          paddingY: 1.25,
        }}
      >
        <Box>
          <Typography variant="body1" fontWeight="bold">
            {question.text}
          </Typography>
          {question.alias && (
            <Typography variant="caption" color="textSecondary">
              Report field: {question.alias}
            </Typography>
          )}
        </Box>
        <Box display="flex" gap={0.75} flexWrap="wrap">
          <Chip label={typeLabel} size="small" variant="outlined" />
          {question.isConvertToTag && <Chip label="Creates tag" size="small" />}
        </Box>
      </Box>
    )
  }

  return (
    <>
      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Overview & Settings
        <Header.Subheader>
          Get a bird’s-eye view of your project at a glance. Track submission progress, view stats, and jump into action
          from one dashboard.
          <br />
          It’s here you can also configure your project settings.
        </Header.Subheader>
      </Header>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8} display="flex">
          <Paper sx={{ ...styles.paper, display: 'flex', flex: 1, width: '100%' }}>
            <Box display="flex" flexDirection="column" gap={2} width="100%">
              <Typography variant="h5" fontWeight="bold">
                Insights
              </Typography>
              <Box
                sx={{
                  display: 'grid',
                  flex: 1,
                  gap: 1.5,
                  gridTemplateColumns: {
                    xs: 'repeat(2, minmax(0, 1fr))',
                    sm: 'repeat(3, minmax(0, 1fr))',
                    lg: 'repeat(5, minmax(0, 1fr))',
                  },
                }}
              >
                {insightCards.map(({ label, value, action, icon, onClick }) => (
                  <Card key={label} variant="outlined" sx={{ display: 'flex', minHeight: 142 }}>
                    <CardActionArea onClick={onClick} aria-label={`${label}: ${action}`}>
                      <CardContent
                        sx={{
                          ...styles.statBox,
                          alignItems: 'center',
                          display: 'flex',
                          flexDirection: 'column',
                          height: '100%',
                          justifyContent: 'center',
                          padding: '14px !important',
                        }}
                      >
                        <Box sx={{ color: '#6b50ff', lineHeight: 0, marginBottom: 0.5 }}>{icon}</Box>
                        <Typography variant="h4" fontWeight="bold">
                          {value}
                        </Typography>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {label}
                        </Typography>
                        <Typography variant="caption" color="primary" sx={{ marginTop: 0.5 }}>
                          {action}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                ))}
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12} md={4} display="flex">
          <InviteCard project={project} />
        </Grid>

        <Grid item xs={12}>
          <Paper sx={styles.paper}>
            <Box display="flex" flexDirection="column" gap={2}>
              <Typography variant="h5" fontWeight="bold">
                Latest Video Submissions
              </Typography>
              {latestEntries.length === 0 ? (
                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <img src={EmptyShelves} width="100%" alt="Empty project video library" />
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <Typography variant="h6" fontWeight="bold">
                      Empty Shelves
                    </Typography>
                    <p>
                      Your project video library is looking a bit bare. Share the link with contributors or upload a
                      test video yourself to bring it to life.
                    </p>
                    <Typography variant="body2">
                      <Link
                        href={projectLinkForQRCode}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: 'none' }}
                      >
                        Test the upload page here
                      </Link>
                    </Typography>
                  </Grid>
                </Grid>
              ) : (
                <>
                  <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', pb: 1, scrollSnapType: 'x mandatory' }}>
                    {latestEntries.map((clip) => (
                      <Card
                        key={clip.id}
                        onClick={() => goToClipPage(clip)}
                        sx={{
                          width: 220,
                          minWidth: 220,
                          maxWidth: 220,
                          flex: '0 0 auto',
                          cursor: 'pointer',
                          scrollSnapAlign: 'start',
                        }}
                      >
                        <CardMedia
                          component="img"
                          image={getOverviewClipImage(clip)}
                          alt={clip.contributor || 'Video'}
                          sx={{ height: 124, objectFit: 'cover' }}
                          onError={(event: React.SyntheticEvent<HTMLImageElement>) =>
                            replaceBrokenOverviewClipImage(event.currentTarget)
                          }
                        />
                        <CardContent>
                          <Typography variant="subtitle1">{clip.contributor || 'Unknown'}</Typography>
                          <Typography variant="subtitle1" color="textSecondary">
                            {moment(clip.createdAt).fromNow()}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>

                  <Button variant="outlined" fullWidth onClick={goToClipGalleryPage}>
                    View all
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        {clipsWithLocation.length > 0 && (
          <Grid item xs={12}>
            <Paper sx={styles.paper}>
              <Typography variant="h5" fontWeight="bold">
                Locations
              </Typography>
              <Segment style={{ width: '100%', height: '300px' }}>
                {Map ? (
                  <Map
                    // react-mapbox-gl uses `style` for the Mapbox style URL.
                    // eslint-disable-next-line react/style-prop-object
                    style="mapbox://styles/mapbox/streets-v8"
                    fitBounds={bbox}
                    fitBoundsOptions={{ padding: 20, maxZoom: 9 }}
                    containerStyle={{ height: '100%', width: '100%' }}
                  >
                    <>
                      <ZoomControl />
                      {clipsWithLocation.map((clip) => (
                        <Layer
                          key={clip.id}
                          type="symbol"
                          id={`marker-${clip.id}`}
                          layout={{ 'icon-image': 'marker-15' }}
                        >
                          <Feature coordinates={[clip.lon1!, clip.lat1!]} />
                        </Layer>
                      ))}
                    </>
                  </Map>
                ) : (
                  <Box
                    sx={{
                      alignItems: 'center',
                      background: '#f6f8fb',
                      borderRadius: 2,
                      color: '#6b7280',
                      display: 'flex',
                      height: '100%',
                      justifyContent: 'center',
                      minHeight: 240,
                      padding: 2,
                      textAlign: 'center',
                      width: '100%',
                    }}
                  >
                    Map preview unavailable in local development.
                  </Box>
                )}
              </Segment>
            </Paper>
          </Grid>
        )}
      </Grid>

      <Header as="h1" style={{ marginBottom: '2rem', fontWeight: 'bold' }}>
        Project Settings
        <Header.Subheader>
          Configure your project details, choose your submission style, set limits and tailor every element of your
          video submission project.
        </Header.Subheader>
      </Header>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Paper sx={styles.paper}>
            <Typography variant="h5" fontWeight="bold">
              Project Overview
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Internal Name"
                  placeholder="Internal Name"
                  name="title"
                  fullWidth
                  value={projectTitle}
                  onChange={handleProjectTitleChange}
                  margin="normal"
                  error={validationError === 'title'}
                  helperText={validationError === 'title' && 'Please give a title to your project'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Public Name"
                  placeholder="Public Name"
                  name="name"
                  fullWidth
                  value={projectName}
                  onChange={handleProjectNameChange}
                  margin="normal"
                  error={validationError === 'name'}
                  helperText={validationError === 'name' && 'Please give a name to your project'}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Reward"
                  placeholder="$500 voucher for the best video"
                  name="reward"
                  fullWidth
                  value={projectReward}
                  onChange={handleProjectRewardChange}
                  margin="normal"
                  error={validationError === 'reward'}
                  helperText={validationError === 'reward' && 'Please provide a reward for your project'}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  select
                  label="Language"
                  value={projectLocale}
                  fullWidth
                  margin="normal"
                  onChange={handleProjectLocaleChange}
                >
                  {locales.map((option, i) => (
                    <MenuItem key={i} value={option.code}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>
                <Typography variant="body2" color="textSecondary">
                  Need help?{' '}
                  <ExternalLink url="https://vloggi.com/docs/how-to-change-contributor-languages-in-vloggi/">
                    Read how to change contributor languages.
                  </ExternalLink>
                </Typography>
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2}>
              <FormControlLabel
                label={isShared ? 'Active' : 'Inactive'}
                control={
                  <Switch
                    checked={isShared}
                    onChange={handleProjectLiveChange}
                    name="shared"
                    sx={{
                      '& .MuiSwitch-switchBase': {
                        color: '#6b50ff !important',
                      },
                    }}
                  />
                }
                sx={{
                  color: isShared ? '#000000' : '#7f7f7f',
                }}
              />
              <Box sx={{ minWidth: 190, textAlign: 'right' }}>
                <Typography variant="body1" textAlign="right">
                  Expiry
                </Typography>
                <ReactDatePicker
                  selected={project.deadline ? moment(project.deadline).toDate() : null}
                  onChange={handleDeadlineChange}
                  isClearable
                  dateFormat="MMMM d, yyyy"
                  placeholderText="Set expiry date"
                  customInput={<ExpiryButton />}
                />
                <Typography variant="caption" color="text.secondary">
                  Set in your local time; shown to contributors in theirs
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <ProjectWorkflowCard project={project} handleChange={handleChange} />
        </Grid>

        <Grid item xs={12} md={8}>
          <Grid container spacing={2}>
            {(isCaptureWorkflow || isConversationWorkflow) && (
              <Grid item xs={12}>
                <Paper sx={styles.paper}>
                  <Typography variant="h5" fontWeight="bold">
                    {isConversationWorkflow ? 'Video Prompts' : 'Recording Prompt'}
                  </Typography>
                  <Typography variant="body1" my={1}>
                    {isConversationWorkflow
                      ? 'Contributors will see each prompt in sequence and record a separate camera response.'
                      : 'Contributors will see this prompt before recording their single camera response.'}
                    &nbsp;
                    <ExternalLink url="https://vloggi.com/docs/building-a-video-questionnaire-in-vloggi-enhancing-engagement-and-insight/">
                      Read more.
                    </ExternalLink>
                  </Typography>

                  {duplicatePromptFields.length > 0 && (
                    <Alert severity="warning" sx={{ marginY: 1 }}>
                      {duplicatePromptFields.length === 1 ? 'A video prompt repeats' : 'Some video prompts repeat'} a
                      contributor data field. Contributors will be asked the same question in video and text:{' '}
                      {duplicatePromptFields
                        .map(({ promptIndex, fieldText }) => `Prompt ${promptIndex + 1} — ${fieldText}`)
                        .join('; ')}
                      . Keep both only when you intentionally need both answer formats.
                    </Alert>
                  )}

                  {isConversationWorkflow && (
                    <Box sx={{ paddingX: 1, paddingTop: 2 }}>
                      <Typography id="prompt-count-slider" gutterBottom>
                        Number of Prompts: <strong>{numberOfQuestions}</strong>
                      </Typography>
                      <Slider
                        aria-labelledby="prompt-count-slider"
                        min={2}
                        max={MAX_NUMBER_OF_QUESTIONS}
                        step={1}
                        marks
                        value={Math.max(2, numberOfQuestions)}
                        valueLabelDisplay="auto"
                        onChange={handlePromptCountChange}
                      />
                    </Box>
                  )}

                  {Array.from({ length: isConversationWorkflow ? Math.max(2, numberOfQuestions) : 1 }).map((_, i) => {
                    const required = isVideoPromptRequired(project.displaySetting, i)

                    return (
                      <Box key={`prompt-${i + 1}`} sx={{ marginTop: 1 }}>
                        <TextField
                          fullWidth
                          margin="normal"
                          label={isConversationWorkflow ? `Prompt ${i + 1}` : 'Prompt'}
                          placeholder="Type your question here"
                          name={`question-${i + 1}`}
                          value={questions[i] || ''}
                          onChange={handleInputChange}
                          inputProps={{ maxLength: MAX_QUESTIONS_CHARACTER_LENGTH }}
                        />
                        {isConversationWorkflow && (
                          <FormControlLabel
                            control={
                              <Switch
                                checked={required}
                                onChange={(event) => handlePromptRequiredChange(event, i, event.target.checked)}
                              />
                            }
                            label={required ? 'Required response' : 'Optional — contributors may skip'}
                          />
                        )}
                      </Box>
                    )
                  })}
                </Paper>
              </Grid>
            )}

            <Grid item xs={12}>
              <Paper sx={{ ...styles.paper, boxSizing: 'border-box', height: '100%' }}>
                <Typography variant="h5" fontWeight="bold">
                  Contributor Data Fields
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ marginY: 1 }}>
                  Define the information collected once from each contributor. Submitted answers appear in the Project
                  Report, separately from prompts and file-specific questions.
                </Typography>

                {projectQuestions.length === 0 ? (
                  <Box
                    sx={{
                      backgroundColor: '#f9fafb',
                      border: '1px dashed #d1d5db',
                      borderRadius: 2,
                      marginY: 2,
                      padding: 2,
                    }}
                  >
                    <Typography variant="body2" color="textSecondary">
                      No additional contributor questions have been configured yet.
                    </Typography>
                  </Box>
                ) : (
                  <Box sx={{ marginY: 2 }}>
                    {contributorQuestions.length > 0 && (
                      <Box sx={{ marginBottom: fileQuestions.length > 0 ? 2 : 0 }}>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Asked once per contributor
                        </Typography>
                        {contributorQuestions.map(renderQuestionSummary)}
                      </Box>
                    )}

                    {fileQuestions.length > 0 && (
                      <Box>
                        <Typography variant="subtitle1" fontWeight="bold">
                          Asked for each file
                        </Typography>
                        {fileQuestions.map(renderQuestionSummary)}
                      </Box>
                    )}
                  </Box>
                )}

                <Button variant="outlined" href={`/projects/${project.slug}/collect?tab=dataCollection`}>
                  Configure Fields
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12} md={4}>
          <FormatsCard project={project} handleChange={handleChange} />
        </Grid>

        <Grid item xs={12} md={4} display="flex">
          <BrandingCard
            project={project}
            handleChangePrimaryFont={handleChangePrimaryFont}
            handleChangePrimaryColor={handleChangePrimaryColor}
          />
        </Grid>

        <Grid item xs={12} md={4} display="flex">
          <SettingsCard project={project} handleChange={handleChange} />
        </Grid>

        <Grid item xs={12} md={4} display="flex">
          <ContestAddonCard project={project} handleChange={handleChange} />
        </Grid>

        <Grid item xs={12}>
          <LegalComplianceCard project={project} handleChange={handleChange} />
        </Grid>
      </Grid>
    </>
  )
}

interface IProps extends RouteComponentProps<any> {
  project: IProject
  clips: IClip[]
  projectQuestions: ProjectQuestionResponse[]
  handleChange: (arg: any, data: any) => void
  handleChangePrimaryFont: (arg: { family: string }) => void
  handleChangePrimaryColor: (arg: { hex: string }) => void
  validationError: string | null
}

export default withRouter(ConfigureTabOverviewPage)
